var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { Button, Col, Row, useEffectUpdate } from '../index';
import { generateSunburstLabels, convertSunburstDatasets, resetItemClickedData, updateChartColours, setClickedSectionToSolid, setSliceClickedData, setBorderColour, updateOpacity, downloadItem } from "./Utils";
import { isPropDefined, getCssVarValue, normaliseCaps } from "../general/Utils";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useState } from "react";
import { themeListener } from "../hooks/listeners";
ChartJS.register(ArcElement, Tooltip, Legend);
function Sunburst(props) {
    var id = props.id, title = props.title, setSliceData = props.setSliceData, legendPosition = props.legendPosition, noDownload = props.noDownload, noLabel = props.noLabel, noRefresh = props.noRefresh, resetChart = props.resetChart;
    var height = props.height ? props.height : "100%";
    var originDatasets = convertSunburstDatasets(props.datasets);
    var _a = useState(originDatasets), datasets = _a[0], setDatasets = _a[1];
    // resets chart on any change
    useEffectUpdate(function () {
        resetItemClickedData(setSliceData);
        setDatasets(originDatasets);
    }, [resetChart]);
    // colours
    var _b = useState(''), titleColour = _b[0], setTitleColour = _b[1];
    themeListener(function () {
        setTitleColour(getCssVarValue("--bs-emphasis-color"));
        // border update doesn't trigger chartjs re-render
        var savedDatasets = datasets;
        setDatasets([{}]);
        setDatasets(setBorderColour(savedDatasets, getCssVarValue("--bs-body-bg")));
    });
    // @ts-ignore
    function handlePlaneClick(event, chartElement, chart, item) {
        if (item !== undefined) {
            return;
        }
        // only clickable if setBarData is defined
        if (isPropDefined(setSliceData)) {
            if (chartElement.length) {
                var _a = chartElement[0], datasetIndex = _a.datasetIndex, index = _a.index;
                var clickKey = chart.data.datasets[datasetIndex].labels[index];
                if (clickKey !== "More" && clickKey !== "Unknown") {
                    // fade non-clicked bars
                    updateChartColours(chart, false, 0.25);
                    // setting clicked bar as its original colour
                    setClickedSectionToSolid(chart, chartElement);
                    setSliceClickedData(chart, chartElement, setSliceData);
                }
            }
            chart.update();
        }
        // workaround for when 'datasets' reset
        setDatasets(chart.data.datasets);
    }
    function handlePlaneHover(event, chartElement) {
        if (isPropDefined(setSliceData)) {
            event.native.target.style.cursor = chartElement[0] ? "pointer" : "default";
            if (chartElement[0]) {
                var _a = chartElement[0], datasetIndex = _a.datasetIndex, index = _a.index;
                var clickKey = event.chart.data.datasets[datasetIndex].labels[index];
                event.native.target.style.cursor = (clickKey !== "More" && clickKey !== "Unknown") ? "pointer" : "default";
            }
        }
    }
    // sunburst options
    var options = {
        animation: false,
        maintainAspectRatio: false,
        responsive: true,
        cutout: "20%",
        devicePixelRatio: 2,
        plugins: {
            // tooltip styling
            tooltip: {
                animation: false,
                usePointStyle: true,
                backgroundColor: "black",
                callbacks: {
                    title: function (context) {
                        var dataPointIndex = context[0].dataIndex;
                        var labels = context[0].dataset.labels;
                        var value = context[0].formattedValue;
                        var percentages = context[0].dataset.percentages;
                        return "".concat(labels[dataPointIndex], ": ").concat(value, " (").concat(percentages[dataPointIndex], "%)");
                    },
                    label: function (context) {
                        if (noLabel)
                            return null;
                        var label = context.dataset.label;
                        return " " + normaliseCaps(label);
                    },
                    labelPointStyle: function () {
                        return {
                            pointStyle: 'rectRounded',
                            rotation: 0
                        };
                    },
                    labelColor: function (context) {
                        var index = context.dataIndex;
                        var colour = updateOpacity(context.dataset.backgroundColor[index], '1');
                        return {
                            backgroundColor: colour,
                            borderColor: colour
                        };
                    },
                }
            },
            legend: {
                display: props.noLegend ? false : true,
                position: legendPosition === undefined ? 'right' : legendPosition,
                onClick: null,
                labels: {
                    padding: 15,
                    usePointStyle: true,
                    generateLabels: function (chart) {
                        return generateSunburstLabels(chart, titleColour);
                    }
                }
            }
        },
        onClick: handlePlaneClick,
        onHover: handlePlaneHover
    };
    var downloadName = props.downloadName !== undefined ? props.downloadName : 'sunburst';
    var showConfigBar = props.title || !noDownload || !noRefresh;
    // adding component sizing
    var paddingBottom = showConfigBar ? "37px" : "0";
    var style = { height: height, paddingBottom: paddingBottom };
    return (_jsxs("div", __assign({ style: style }, { children: [showConfigBar &&
                _jsxs(Row, { children: [_jsx(Col, __assign({ xs: 6 }, { children: _jsx("p", __assign({ className: "chart-header-text" }, { children: title })) })), _jsx(Col, __assign({ xs: 6 }, { children: _jsxs("div", __assign({ className: "tol-chart-buttons" }, { children: [isPropDefined(setSliceData) && !noRefresh &&
                                        _jsx("div", { children: _jsx(Button, __assign({ className: "config-button-right", variant: "primary", onClick: function () {
                                                    resetItemClickedData(setSliceData);
                                                    setDatasets(originDatasets);
                                                } }, { children: _jsx(FontAwesomeIcon, { icon: faUndo, size: "sm" }) })) }), !noDownload &&
                                        _jsx("div", { children: _jsx(Button, __assign({ className: "config-button-right", variant: "primary", onClick: function () {
                                                    downloadItem(props.id, downloadName);
                                                } }, { children: _jsx(FontAwesomeIcon, { icon: faDownload, size: "sm" }) })) })] })) }))] }), _jsx(Doughnut, { id: id, responsive: "true", className: "tol-sunburst", datasetIdKey: "id", 
                // @ts-ignore
                options: options, data: { datasets: datasets } })] })));
}
export default Sunburst;
